import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

/** Geometria da pilha (constantes do handoff, não tokens globais). */
const PEEK = 10;
const INSET = 14;
const MAX_VIS = 3;
// Janela total do stagger (s): num deck longo o atraso por card é comprimido p/
// caber aqui, senão os cards "pingariam" por muito tempo. Deck curto usa o passo
// cheio. Pareado com a duração do `layout` no consumidor.
const STAGGER_STEP_S = 0.035;
const STAGGER_WINDOW_S = 0.28;

function stackHeight(cards: HTMLElement[], peek: number): string {
  const top = cards[0];
  if (!top) return '';
  const visBehind = Math.min(cards.length - 1, MAX_VIS - 1);
  return `${String(top.offsetHeight + visBehind * peek)}px`;
}

/** Fixa (empilhado) ou solta a altura da lista; devolve o card do topo da pilha. */
function syncStack(
  list: HTMLElement,
  cardSelector: string,
  stacked: boolean,
  peek: number,
): HTMLElement | undefined {
  const top = list.querySelector<HTMLElement>(cardSelector);
  if (stacked && top) {
    list.style.height = stackHeight(
      Array.from(list.querySelectorAll<HTMLElement>(cardSelector)),
      peek,
    );
    list.style.setProperty('--stack-card-h', `${String(top.offsetHeight)}px`);
    return top;
  }
  list.style.height = '';
  list.style.removeProperty('--stack-card-h');
  return undefined;
}

/**
 * Deck empilhado (collapsed) ↔ lista (open) — genérico (viagens, locação…). A
 * animação de POSIÇÃO é do `motion` (prop `layout` em cada card — FLIP robusto,
 * sem timing manual de cleanup); o hook só gere o estado e fixa a altura da
 * pilha, já que os cards empilhados são `position:absolute` e não somam altura.
 * `cardSelector` é o seletor CSS dos cards dentro da lista (ex.: `.tcard`,
 * `.rui-deck-card`). A pilha só se aplica com mais de um card; em telas estreitas
 * a altura recalcula no resize (o conteúdo reflua), e a troca dos cards (outra
 * ordem, outro layout) é observada no DOM da lista.
 */
export function useStackedDeck(
  count: number,
  cardSelector: string,
  /** Nasce aberto, para quem monta a pilha JÁ recolhendo (o recibo do carro,
   *  #5642: a lista aparece e encolhe sob a escolha com `collapse`). */
  initiallyExpanded = false,
  /** Quanto cada card de trás aparece abaixo do topo — o `PEEK` do handoff
   *  quando o consumidor não desenha a sua pilha. */
  peek = PEEK,
) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const listRef = useRef<HTMLDivElement>(null);
  const stacked = count > 1 && !expanded;

  const expand = () => {
    setExpanded(true);
  };
  const collapse = () => {
    setExpanded(false);
  };
  const toggle = () => {
    setExpanded((e) => !e);
  };

  // Empilhado: cards absolutos não somam altura — fixa no card do topo + os
  // "peeks" visíveis. Aberto (ou card único): altura automática. Expõe a altura
  // do card do topo p/ shells vazios atrás (mesma geometria, sem conteúdo).
  // ResizeObserver recalcula quando a thumb carrega ou o card reflua (telas
  // estreitas); o MutationObserver, quando os cards da lista trocam (outra
  // contagem, outra ordem), já com o topo novo observado.
  const flushRef = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const ro = new ResizeObserver(() => {
      syncStack(list, cardSelector, stacked, peek);
    });
    const sync = () => {
      const top = syncStack(list, cardSelector, stacked, peek);
      ro.disconnect();
      if (top) ro.observe(top);
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(list, { childList: true, subtree: true });
    // A troca que veio num commit deste deck mede no próprio commit, antes da
    // pintura — sem esperar o microtask do observer.
    flushRef.current = () => {
      if (mo.takeRecords().length > 0) sync();
    };
    return () => {
      flushRef.current = null;
      mo.disconnect();
      ro.disconnect();
    };
  }, [stacked, cardSelector, peek]);
  useLayoutEffect(() => {
    flushRef.current?.();
  });

  // Recalcula a altura empilhada quando o card reflua em telas estreitas.
  useEffect(() => {
    if (!stacked) return undefined;
    const onResize = () => {
      const list = listRef.current;
      const cards = list ? Array.from(list.querySelectorAll<HTMLElement>(cardSelector)) : [];
      if (list && cards[0]) {
        list.style.height = stackHeight(cards, peek);
        list.style.setProperty('--stack-card-h', `${String(cards[0].offsetHeight)}px`);
      }
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [stacked, cardSelector, peek]);

  return { expanded, stacked, listRef, expand, collapse, toggle };
}

/**
 * Atraso (s) do stagger por card no glide do `layout`. Comprime o passo p/ caber
 * em `STAGGER_WINDOW_S` quando o deck é longo; ao abrir cascateia de cima p/
 * baixo, ao recolher de baixo p/ cima.
 */
export function stackStagger(index: number, count: number, expanded: boolean): number {
  if (count <= 1) return 0;
  const step = Math.min(STAGGER_STEP_S, STAGGER_WINDOW_S / (count - 1));
  return (expanded ? index : count - 1 - index) * step;
}

/** Card oculto na pilha (atrás dos `MAX_VIS` visíveis) → opacidade 0. */
export function isHiddenInStack(index: number): boolean {
  return index > MAX_VIS - 1;
}

/** Quanto cada carta de trás desce (`peek`) e recua de cada lado (`inset`), em px. */
export type StackGeometry = { readonly peek: number; readonly inset: number };

const DEFAULT_GEOMETRY: StackGeometry = { peek: PEEK, inset: INSET };

/** Offsets inline que montam a pilha — puro, por índice (geometria do handoff, ou a do
 *  consumidor que desenha a sua; o mesmo `peek` vai para o `useStackedDeck`).
 *  A opacidade dos cards ocultos é animada pelo `motion` (prop `animate`), não aqui. */
export function stackedCardStyle(
  i: number,
  count: number,
  { peek, inset }: StackGeometry = DEFAULT_GEOMETRY,
): CSSProperties {
  const lvl = Math.min(i, MAX_VIS - 1);
  return {
    top: lvl * peek,
    left: lvl * inset,
    right: lvl * inset,
    zIndex: count - i,
  };
}
